import React, { PropsWithChildren, useRef, useState } from 'react';
import {
  Box,
  Divider,
  Flex,
  Icon,
  RestrictedButton,
  IconOverride,
  Spinner,
  Popover,
  IconButton,
  Text,
  OverflowText,
} from '@fullstory/components';
import css from './index.module.scss';
import { MetadataDisplay } from '../MetadataDisplay';
import { intl } from '@fullstory/intl';
import { classNames } from '@fullstory/util/fmt';
export interface ResourceDisplayPaneProps {
  tooltip?: boolean;
  className?: string;
  staticHeight?: boolean;
}

function ResourceDisplayPane({
  children,
  tooltip = false,
  className,
  staticHeight = true,
}: React.PropsWithChildren<ResourceDisplayPaneProps>) {
  return (
    <Flex
      data-component="ResourceDisplayPane"
      column
      alignItems="stretch"
      justifyContent="space-between"
      className={classNames(
        {
          [css.definitionTooltip]: tooltip,
          [css.definition]: !tooltip,
          [css.staticHeight]: staticHeight,
        },
        className,
      )}
    >
      {children}
    </Flex>
  );
}

export interface ResourceDisplayPaneHeaderProps {
  label: string;
  icon?: React.ComponentPropsWithRef<typeof Icon>['name'] | IconOverride;
  onOpen?: () => void;
  isView?: boolean;
  canAccess?: boolean | 'loading';
  /** Optional CTA rendered next to the edit CTA */
  actionComponent?: React.ReactNode;
  testId?: string;
}

export const ResourceDisplayPaneHeader: React.FC<ResourceDisplayPaneHeaderProps> = ({
  label,
  icon,
  onOpen,
  isView,
  canAccess,
  actionComponent,
  testId,
}) => {
  const anchor = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const editLabel = isView
    ? intl.formatMessage({ defaultMessage: 'View', id: 'FgydNe' })
    : intl.formatMessage({ defaultMessage: 'Edit', id: 'wEQDC6' });

  return (
    <Box data-component="ResourceDisplayPaneHeader" flexGrow={0}>
      <Box padding={8}>
        <Flex justifyContent="space-between">
          <Box padding={8} overflow="hidden">
            <Flex alignItems="center">
              {icon && (
                <Box marginRight={16}>
                  {typeof icon === 'string' ? (
                    <Icon name={icon} color="secondary" size={20} />
                  ) : (
                    <Icon {...icon} />
                  )}
                </Box>
              )}
              <Text appearance="bodyMediumBold" color="emphasized" noWrap tag="span">
                <OverflowText>{label}</OverflowText>
              </Text>
            </Flex>
          </Box>
          <Box paddingRight={8}>
            {canAccess === 'loading' ? (
              <Spinner />
            ) : actionComponent ? (
              <Flex>
                <Box paddingRight={4}>{actionComponent}</Box>
                {onOpen && (
                  <>
                    <IconButton
                      ref={anchor}
                      iconName="ellipsis-vertical"
                      title={intl.formatMessage({
                        defaultMessage: 'More actions',
                        id: 'S8/4ZI',
                      })}
                      onClick={e => {
                        open ? setOpen(false) : setOpen(true);
                        e.stopPropagation(); // Prevent click events from bubbling up to parent
                      }}
                      size="large"
                      tooltipPlacement="right"
                    />
                    <Popover
                      anchor={anchor.current}
                      open={open}
                      placement="bottom-end"
                      offset={8}
                      flipY={false}
                      portal={false}
                      positioningStrategy="fixed"
                      variant="bordered"
                    >
                      <RestrictedButton
                        variant={canAccess ? 'minimal' : 'fill-primary'}
                        disabled={!canAccess}
                        onClick={e => {
                          onOpen();
                          e.stopPropagation(); // Prevent click events from bubbling up to parent
                        }}
                        lockPosition="left"
                        testId={testId}
                      >
                        {editLabel}
                      </RestrictedButton>
                    </Popover>
                  </>
                )}
              </Flex>
            ) : (
              onOpen && (
                <RestrictedButton
                  variant={canAccess ? 'minimal' : 'fill-primary'}
                  disabled={!canAccess}
                  onClick={e => {
                    onOpen();
                    e.stopPropagation(); // Prevent click events from bubbling up to parent
                  }}
                  lockPosition="left"
                  testId={testId || `resource-display-pane-header-button`}
                >
                  {editLabel}
                </RestrictedButton>
              )
            )}
          </Box>
        </Flex>
      </Box>
      <Divider />
    </Box>
  );
};

export interface ResourceDisplayPaneBodyProps {
  testId?: string;
}

export const ResourceDisplayPaneBody: React.FC<PropsWithChildren<ResourceDisplayPaneBodyProps>> = ({
  testId = 'resource-display-pane-body',
  children,
}) => (
  <Box
    data-component="ResourceDisplayPaneBody"
    testId={testId}
    padding={16}
    overflow="auto"
    flexGrow={1}
  >
    {children}
  </Box>
);

export interface ResourceDisplayPaneFooterProps
  extends Omit<
    React.ComponentProps<typeof MetadataDisplay>,
    'disableBottomBorder' | 'disableTopBorder'
  > {
  testId?: string;
}

export const ResourceDisplayPaneFooter: React.FC<ResourceDisplayPaneFooterProps> = ({
  testId = 'resource-display-pane-footer',
  ...metadataDisplayProps
}) => (
  <Box data-component="ResourceDisplayPaneFooter" testId={testId} flexGrow={0} minHeight={66}>
    <MetadataDisplay {...metadataDisplayProps} disableBottomBorder disableTopBorder />
  </Box>
);

const ResourceDisplayPaneWithStatics = ResourceDisplayPane as typeof ResourceDisplayPane & {
  Header: typeof ResourceDisplayPaneHeader;
  Body: typeof ResourceDisplayPaneBody;
  Footer: typeof ResourceDisplayPaneFooter;
};

ResourceDisplayPaneWithStatics.Header = ResourceDisplayPaneHeader;
ResourceDisplayPaneWithStatics.Body = ResourceDisplayPaneBody;
ResourceDisplayPaneWithStatics.Footer = ResourceDisplayPaneFooter;
export { ResourceDisplayPaneWithStatics as ResourceDisplayPane };
